/* eslint-disable @typescript-eslint/no-floating-promises */
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { dropTask, timeout } from 'ember-concurrency';
import { or } from 'ember-truth-helpers';
import WrButton, {
  type WrButtonSignature
} from 'ember-wiser/components/button';
import type JournalEventModel from 'parog-web/models/journal-event';
import type { Signature as LinkButtonSignature } from 'ui-kit/components/link-button';
import LinkButton from 'ui-kit/components/link-button';
import emberSwal from 'ui-kit/utils/ember-swal';

export interface ArchiveJournalEventButtonSignature {
  Element: LinkButtonSignature['Element'];
  Args: {
    iconSize?: string;
    /** Opt-in the new Wiser design system if the component cannot be migrated immediately */
    shouldUseWiser?: boolean;
    /** The Wiser button variant, only used with `shouldUseWiser` */
    variant?: WrButtonSignature['Args']['variant'];
    beforeArchive?: () => unknown;
    afterArchived?: () => void;
    confirmationTitle?: string;
    additionalInformation?: string;
    journalEvent: JournalEventModel;
  };
  Blocks: { default: [] };
}

export default class ArchiveJournalEventButton extends Component<ArchiveJournalEventButtonSignature> {
  confirmArchive = async event => {
    event.stopPropagation();

    if (this.deleteJournalEventTask.isRunning) return;

    const result = await emberSwal.fire({
      title: this.args.confirmationTitle || 'Confirmer la suppression',
      text:
        this.args.additionalInformation ||
        'Cette opération ne peut pas être annulée.',
      icon: 'warning',
      cancelButtonText: 'Annuler',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer'
    });

    if (result.value) {
      this.deleteJournalEventTask.perform(this.args.journalEvent);
    }
  };

  deleteJournalEventTask = dropTask(async (je: JournalEventModel) => {
    // This is just to help understand what's going on.
    await timeout(200);
    await this.args.beforeArchive?.();
    await je.archive();
    this.args.afterArchived?.();
  });

  <template>
    {{#if @shouldUseWiser}}
      <WrButton
        @icon='Trash'
        @variant={{or @variant 'outlined-mini'}}
        {{on 'click' this.confirmArchive}}
        aria-label='archiver le JE'
        ...attributes
      />
    {{else}}
      <LinkButton
        data-tooltip={{unless (has-block) 'Supprimer'}}
        aria-label='archiver le JE'
        {{on 'click' this.confirmArchive}}
        ...attributes
      >
        {{#if this.deleteJournalEventTask.isRunning}}
          <i class={{concat 'notched circle loading icon' @iconSize}}></i>
        {{else}}
          <i class={{concat 'trash alternate icon outline' @iconSize}}></i>
        {{/if}}
        {{yield}}
      </LinkButton>
    {{/if}}
  </template>
}
